import { compareLocalDates } from './dates';
import {
  absoluteMoney,
  addMoney,
  assertMoney,
  assertNonNegativeMoney,
  assertPositiveMoney,
  ratioBasisPoints,
  subtractMoney,
} from './money';
import type {
  AccountPosition,
  CashFlowEvent,
  CommitmentLevel,
  ForecastInput,
  ForecastResult,
  LocalDate,
  Money,
  ReportingClass,
  TimelinePoint,
  TransactionType,
} from './types';

interface NormalizedEvent {
  event: CashFlowEvent;
  effectiveDate: LocalDate;
  magnitude: Money;
  rank: number;
  assumption?: string;
}

interface Snapshot {
  accessibleHeadroom: Money;
  unprotectedLiquid: Money;
  safeToSpend: Money;
  belowSafetyBuffer: boolean;
  negativeAccessible: boolean;
  negativeUnprotected: boolean;
  accountMinimumBreach: boolean;
  negativeAccountBalance: boolean;
  goalReservedBalanceBreach: boolean;
}

export function buildForecast(input: ForecastInput): ForecastResult {
  validateForecastInput(input);
  const accounts = new Map(input.accounts.map((account) => [account.id, account]));
  const balances = new Map(input.accounts.map((account) => [account.id, account.balance]));
  const reserved = new Map(
    input.accounts.map((account) => [account.id, account.reservedBalance ?? 0]),
  );
  const includeProtected = input.includeProtected ?? false;
  const opening = calculateSnapshot(
    input.accounts,
    balances,
    reserved,
    input.safetyBuffer,
    includeProtected,
  );

  let minimumAccessibleBalance = opening.accessibleHeadroom;
  let minimumDate = input.from;
  let minimumUnprotectedLiquidBalance = opening.unprotectedLiquid;
  let minimumUnprotectedLiquidDate = input.from;
  let mandatoryShortfall = false;
  let safetyBufferBreach = opening.belowSafetyBuffer;
  let accountMinimumBreach = opening.accountMinimumBreach;
  let negativeAccountBalanceBreach = opening.negativeAccountBalance;
  let accountFundingGap = false;
  let protectedAccountUsed = false;
  let goalReservedBalanceBreach = opening.goalReservedBalanceBreach;
  const assumptions: string[] = [];

  const normalizedEvents: NormalizedEvent[] = [];
  for (const event of input.events) {
    if (event.settled) continue;
    const normalized = normalizeEvent(event, input.from, input.to);
    if (normalized.excludedAssumption) assumptions.push(normalized.excludedAssumption);
    if (normalized.event) normalizedEvents.push(normalized.event);
  }
  normalizedEvents.sort(compareNormalizedEvents);

  const points: TimelinePoint[] = [];
  for (const normalized of normalizedEvents) {
    const { event, effectiveDate, magnitude } = normalized;
    if (normalized.assumption) assumptions.push(normalized.assumption);
    const commitment = commitmentFor(event);
    const sourceId = sourceAccountFor(event);
    const destinationId = destinationAccountFor(event);
    let eventFundingGap = false;
    let eventMandatoryShortfall = false;
    let eventProtectedUse = false;
    let primaryAccountId: string | undefined;

    if (event.kind === 'INCOME' || event.kind === 'DEBT_INCOME' || event.kind === 'TRANSFER_IN') {
      const accountId = requireAccount(destinationId, event, 'destination');
      ensureAccountCurrency(accounts.get(accountId), event);
      balances.set(accountId, addMoney(requireBalance(balances, accountId), magnitude));
      primaryAccountId = accountId;
    } else if (
      event.kind === 'EXPENSE' ||
      event.kind === 'DEBT_EXPENSE' ||
      event.kind === 'TRANSFER_OUT'
    ) {
      const accountId = requireAccount(sourceId, event, 'source');
      const account = requireAccountRecord(accounts, accountId);
      ensureAccountCurrency(account, event);
      const funding = fundingCapacity(
        accountId,
        magnitude,
        input.accounts,
        balances,
        includeProtected,
      );
      eventFundingGap = funding.accountGap && !funding.hardShortfall;
      eventMandatoryShortfall = commitment === 'MANDATORY' && funding.hardShortfall;
      eventProtectedUse = account.protected;
      balances.set(accountId, subtractMoney(requireBalance(balances, accountId), magnitude));
      primaryAccountId = accountId;
    } else if (event.kind === 'TRANSFER') {
      const fromAccountId = requireAccount(sourceId, event, 'source');
      const toAccountId = requireAccount(destinationId, event, 'destination');
      if (fromAccountId === toAccountId)
        throw new Error(`Transfer ${event.id} accounts must differ`);
      const source = requireAccountRecord(accounts, fromAccountId);
      const destination = requireAccountRecord(accounts, toAccountId);
      ensureAccountCurrency(source, event);
      ensureAccountCurrency(destination, event);
      if (source.currency && destination.currency && source.currency !== destination.currency) {
        throw new Error(`Transfer ${event.id} cannot cross currencies`);
      }
      const funding = fundingCapacity(
        fromAccountId,
        magnitude,
        input.accounts,
        balances,
        includeProtected,
      );
      eventFundingGap = funding.accountGap && !funding.hardShortfall;
      eventMandatoryShortfall = commitment === 'MANDATORY' && funding.hardShortfall;
      eventProtectedUse = source.protected;
      balances.set(
        fromAccountId,
        subtractMoney(requireBalance(balances, fromAccountId), magnitude),
      );
      balances.set(toAccountId, addMoney(requireBalance(balances, toAccountId), magnitude));
      primaryAccountId = fromAccountId;
    } else {
      const accountId = requireAccount(sourceId, event, 'allocation');
      const account = requireAccountRecord(accounts, accountId);
      ensureAccountCurrency(account, event);
      reserved.set(accountId, addMoney(reserved.get(accountId) ?? 0, magnitude));
      eventProtectedUse = account.protected;
      primaryAccountId = accountId;
    }

    const snapshot = calculateSnapshot(
      input.accounts,
      balances,
      reserved,
      input.safetyBuffer,
      includeProtected,
    );
    if (snapshot.accessibleHeadroom < minimumAccessibleBalance) {
      minimumAccessibleBalance = snapshot.accessibleHeadroom;
      minimumDate = effectiveDate;
    }
    if (snapshot.unprotectedLiquid < minimumUnprotectedLiquidBalance) {
      minimumUnprotectedLiquidBalance = snapshot.unprotectedLiquid;
      minimumUnprotectedLiquidDate = effectiveDate;
    }

    mandatoryShortfall ||= eventMandatoryShortfall;
    safetyBufferBreach ||= snapshot.belowSafetyBuffer;
    accountMinimumBreach ||= snapshot.accountMinimumBreach;
    negativeAccountBalanceBreach ||= snapshot.negativeAccountBalance;
    accountFundingGap ||= eventFundingGap;
    protectedAccountUsed ||= eventProtectedUse;
    goalReservedBalanceBreach ||= snapshot.goalReservedBalanceBreach;

    points.push({
      event: { ...event, date: effectiveDate },
      effectiveDate,
      totalAccessibleBalance: snapshot.accessibleHeadroom,
      unprotectedLiquidBalance: snapshot.unprotectedLiquid,
      safeToSpend: snapshot.safeToSpend,
      ...(primaryAccountId ? { accountBalance: requireBalance(balances, primaryAccountId) } : {}),
      accountBalances: balancesRecord(balances),
      belowSafetyBuffer: snapshot.belowSafetyBuffer,
      negativeAccessibleCash: snapshot.negativeAccessible,
      negativeUnprotectedCash: snapshot.negativeUnprotected,
      accountBelowMinimum: snapshot.accountMinimumBreach,
      negativeAccountBalance: snapshot.negativeAccountBalance,
      accountFundingGap: eventFundingGap,
      protectedAccountUsed: eventProtectedUse,
      goalReservedBalanceBreach: snapshot.goalReservedBalanceBreach,
    });
  }

  const ending = calculateSnapshot(
    input.accounts,
    balances,
    reserved,
    input.safetyBuffer,
    includeProtected,
  );
  return {
    openingAccessibleBalance: opening.accessibleHeadroom,
    endingAccessibleBalance: ending.accessibleHeadroom,
    minimumAccessibleBalance,
    minimumDate,
    openingUnprotectedLiquidBalance: opening.unprotectedLiquid,
    endingUnprotectedLiquidBalance: ending.unprotectedLiquid,
    minimumUnprotectedLiquidBalance,
    minimumUnprotectedLiquidDate,
    endingSafeToSpend: ending.safeToSpend,
    points,
    orderedEvents: points.map((point) => point.event),
    endingAccountBalances: balancesRecord(balances),
    mandatoryShortfall,
    safetyBufferBreach,
    accountMinimumBreach,
    negativeAccountBalanceBreach,
    accountFundingGap,
    protectedAccountUsed,
    goalReservedBalanceBreach,
    assumptions: [...new Set(assumptions)],
  };
}

export function transactionEffect(
  type: TransactionType,
  amount: Money,
  reportingClass: ReportingClass = 'STANDARD',
): { income: Money; expense: Money; debtInflow: Money; debtOutflow: Money } {
  assertPositiveMoney(amount, 'Transaction amount');
  if (type === 'TRANSFER') return { income: 0, expense: 0, debtInflow: 0, debtOutflow: 0 };
  if (reportingClass !== 'STANDARD') {
    return type === 'INCOME'
      ? { income: 0, expense: 0, debtInflow: amount, debtOutflow: 0 }
      : { income: 0, expense: 0, debtInflow: 0, debtOutflow: amount };
  }
  return type === 'INCOME'
    ? { income: amount, expense: 0, debtInflow: 0, debtOutflow: 0 }
    : { income: 0, expense: amount, debtInflow: 0, debtOutflow: 0 };
}

export function calculateMonthlyMetrics(
  transactions: ReadonlyArray<{
    type: TransactionType;
    amount: Money;
    reportingClass?: ReportingClass;
    voided?: boolean;
  }>,
) {
  let income: Money = 0;
  let expenses: Money = 0;
  let debtInflows: Money = 0;
  let debtOutflows: Money = 0;
  for (const transaction of transactions) {
    if (transaction.voided) continue;
    const effect = transactionEffect(
      transaction.type,
      transaction.amount,
      transaction.reportingClass ?? 'STANDARD',
    );
    income = addMoney(income, effect.income);
    expenses = addMoney(expenses, effect.expense);
    debtInflows = addMoney(debtInflows, effect.debtInflow);
    debtOutflows = addMoney(debtOutflows, effect.debtOutflow);
  }
  const surplus = subtractMoney(income, expenses);
  return {
    income,
    expenses,
    surplus,
    surplusRateBasisPoints: ratioBasisPoints(surplus, income),
    debtInflows,
    debtOutflows,
  };
}

export function accountBalance(
  openingBalance: Money,
  transactions: ReadonlyArray<{
    type: TransactionType;
    amount: Money;
    accountId?: string;
    fromAccountId?: string;
    toAccountId?: string;
    voided?: boolean;
  }>,
  accountId: string,
): Money {
  assertMoney(openingBalance, 'Opening balance');
  return transactions.reduce<Money>((balance, transaction) => {
    if (transaction.voided) return balance;
    assertPositiveMoney(transaction.amount, 'Transaction amount');
    if (transaction.type === 'INCOME' && transaction.accountId === accountId) {
      return addMoney(balance, transaction.amount);
    }
    if (transaction.type === 'EXPENSE' && transaction.accountId === accountId) {
      return subtractMoney(balance, transaction.amount);
    }
    if (transaction.type === 'TRANSFER' && transaction.fromAccountId === accountId) {
      return subtractMoney(balance, transaction.amount);
    }
    if (transaction.type === 'TRANSFER' && transaction.toAccountId === accountId) {
      return addMoney(balance, transaction.amount);
    }
    return balance;
  }, openingBalance);
}

export function commitmentFor(event: CashFlowEvent): CommitmentLevel {
  if (event.commitmentLevel) return event.commitmentLevel;
  if (event.mandatory) return 'MANDATORY';
  if (event.kind === 'GOAL_ALLOCATION') return 'COMMITTED';
  return 'DISCRETIONARY';
}

export function isInflow(event: CashFlowEvent): boolean {
  return event.kind === 'INCOME' || event.kind === 'DEBT_INCOME' || event.kind === 'TRANSFER_IN';
}

export function isOutflow(event: CashFlowEvent): boolean {
  return (
    event.kind === 'EXPENSE' ||
    event.kind === 'DEBT_EXPENSE' ||
    event.kind === 'TRANSFER_OUT' ||
    event.kind === 'TRANSFER' ||
    event.kind === 'GOAL_ALLOCATION'
  );
}

function validateForecastInput(input: ForecastInput): void {
  if (compareLocalDates(input.from, input.to) > 0) throw new Error('Forecast range is invalid');
  assertNonNegativeMoney(input.safetyBuffer, 'Safety buffer');
  const ids = new Set<string>();
  for (const account of input.accounts) {
    if (ids.has(account.id)) throw new Error(`Duplicate account: ${account.id}`);
    ids.add(account.id);
    assertMoney(account.balance, `Balance for ${account.id}`);
    assertNonNegativeMoney(account.minimumBalance, `Minimum balance for ${account.id}`);
    assertNonNegativeMoney(account.reservedBalance ?? 0, `Reserved balance for ${account.id}`);
  }
}

function normalizeEvent(
  event: CashFlowEvent,
  from: LocalDate,
  to: LocalDate,
): { event?: NormalizedEvent; excludedAssumption?: string } {
  assertMoney(event.amount, `Event ${event.id} amount`);
  const magnitude = absoluteMoney(event.amount);
  assertPositiveMoney(magnitude, `Event ${event.id} amount`);

  const dateResult = resolveEventDate(event, from);
  if (dateResult.excludedAssumption) return { excludedAssumption: dateResult.excludedAssumption };
  const effectiveDate = dateResult.date;
  if (!effectiveDate || compareLocalDates(effectiveDate, to) > 0) return {};
  return {
    event: {
      event,
      effectiveDate,
      magnitude,
      rank: eventRank(event, dateResult.wasOverdue),
      ...(dateResult.assumption ? { assumption: dateResult.assumption } : {}),
    },
  };
}

function resolveEventDate(
  event: CashFlowEvent,
  from: LocalDate,
): {
  date?: LocalDate;
  wasOverdue: boolean;
  assumption?: string;
  excludedAssumption?: string;
} {
  if (event.dueWindowStart || event.dueWindowEnd) {
    if (!event.dueWindowStart || !event.dueWindowEnd) {
      throw new Error(`Event ${event.id} has an incomplete due window`);
    }
    if (compareLocalDates(event.dueWindowStart, event.dueWindowEnd) > 0) {
      throw new Error(`Event ${event.id} has an invalid due window`);
    }
    if (
      event.scheduledDate &&
      (compareLocalDates(event.scheduledDate, event.dueWindowStart) < 0 ||
        compareLocalDates(event.scheduledDate, event.dueWindowEnd) > 0)
    ) {
      throw new Error(`Event ${event.id} scheduled date falls outside its due window`);
    }
  }

  const selected = event.scheduledDate
    ? event.scheduledDate
    : event.dueWindowStart && event.dueWindowEnd
      ? isInflow(event)
        ? event.dueWindowEnd
        : event.dueWindowStart
      : event.date;
  if (!selected) throw new Error(`Event ${event.id} requires a date or due window`);

  const fallbackAssumption =
    !event.scheduledDate && event.dueWindowStart && event.dueWindowEnd
      ? `${event.label}: ${isInflow(event) ? 'income uses due-window end' : 'outflow uses due-window start'} (${selected}).`
      : undefined;
  if (compareLocalDates(selected, from) >= 0) {
    return {
      date: selected,
      wasOverdue: event.overdue ?? false,
      ...(fallbackAssumption ? { assumption: fallbackAssumption } : {}),
    };
  }
  if (isInflow(event)) {
    return {
      wasOverdue: true,
      excludedAssumption: `${event.label}: overdue expected income was excluded until rescheduled.`,
    };
  }
  return {
    date: from,
    wasOverdue: true,
    assumption: `${event.label}: overdue outflow was carried to ${from}.`,
  };
}

function eventRank(event: CashFlowEvent, overdue: boolean): number {
  if (isInflow(event)) return 60;
  if (overdue || event.overdue) return 10;
  if (commitmentFor(event) === 'MANDATORY') return 20;
  if (event.isProposal) return 30;
  if (commitmentFor(event) === 'COMMITTED') return 40;
  return 50;
}

function compareNormalizedEvents(left: NormalizedEvent, right: NormalizedEvent): number {
  return (
    compareLocalDates(left.effectiveDate, right.effectiveDate) ||
    left.rank - right.rank ||
    compareLocalDates(
      left.event.dueWindowEnd ?? left.effectiveDate,
      right.event.dueWindowEnd ?? right.effectiveDate,
    ) ||
    left.event.id.localeCompare(right.event.id)
  );
}

function sourceAccountFor(event: CashFlowEvent): string | undefined {
  return event.sourceAccountId ?? event.accountId;
}

function destinationAccountFor(event: CashFlowEvent): string | undefined {
  return event.destinationAccountId ?? event.accountId;
}

function requireAccount(value: string | undefined, event: CashFlowEvent, role: string): string {
  if (!value) throw new Error(`Event ${event.id} requires a ${role} account`);
  return value;
}

function requireAccountRecord(
  accounts: ReadonlyMap<string, AccountPosition>,
  accountId: string,
): AccountPosition {
  const account = accounts.get(accountId);
  if (!account) throw new Error(`Unknown account: ${accountId}`);
  return account;
}

function requireBalance(balances: ReadonlyMap<string, Money>, accountId: string): Money {
  const balance = balances.get(accountId);
  if (balance === undefined) throw new Error(`Unknown account: ${accountId}`);
  return balance;
}

function ensureAccountCurrency(account: AccountPosition | undefined, event: CashFlowEvent): void {
  if (!account) throw new Error(`Event ${event.id} references an unknown account`);
  if (event.currency && account.currency && event.currency !== account.currency) {
    throw new Error(`Event ${event.id} currency does not match account ${account.id}`);
  }
}

function eligible(account: AccountPosition, includeProtected: boolean): boolean {
  return account.active !== false && account.spendable && (includeProtected || !account.protected);
}

function calculateSnapshot(
  accounts: readonly AccountPosition[],
  balances: ReadonlyMap<string, Money>,
  reserved: ReadonlyMap<string, Money>,
  safetyBuffer: Money,
  includeProtected: boolean,
): Snapshot {
  let accessibleHeadroom: Money = 0;
  let unprotectedLiquid: Money = 0;
  let accountMinimumBreach = false;
  let negativeAccountBalance = false;
  let goalReservedBalanceBreach = false;
  for (const account of accounts) {
    const balance = requireBalance(balances, account.id);
    const reservedAmount = reserved.get(account.id) ?? 0;
    accountMinimumBreach ||= balance < account.minimumBalance;
    negativeAccountBalance ||= balance < 0;
    goalReservedBalanceBreach ||= reservedAmount > Math.max(0, balance);
    if (!eligible(account, includeProtected)) continue;
    unprotectedLiquid = addMoney(unprotectedLiquid, balance);
    accessibleHeadroom = addMoney(
      accessibleHeadroom,
      subtractMoney(subtractMoney(balance, account.minimumBalance), reservedAmount),
    );
  }
  return {
    accessibleHeadroom,
    unprotectedLiquid,
    safeToSpend: Math.max(0, subtractMoney(accessibleHeadroom, safetyBuffer)),
    belowSafetyBuffer: accessibleHeadroom < safetyBuffer,
    negativeAccessible: accessibleHeadroom < 0,
    negativeUnprotected: unprotectedLiquid < 0,
    accountMinimumBreach,
    negativeAccountBalance,
    goalReservedBalanceBreach,
  };
}

function fundingCapacity(
  sourceAccountId: string,
  amount: Money,
  accounts: readonly AccountPosition[],
  balances: ReadonlyMap<string, Money>,
  includeProtected: boolean,
): { accountGap: boolean; hardShortfall: boolean } {
  const sourceBalance = requireBalance(balances, sourceAccountId);
  const accountGap = sourceBalance < amount;
  let capacity = Math.max(0, sourceBalance);
  for (const account of accounts) {
    if (account.id === sourceAccountId || !eligible(account, includeProtected)) continue;
    capacity = addMoney(capacity, Math.max(0, requireBalance(balances, account.id)));
  }
  return { accountGap, hardShortfall: capacity < amount };
}

function balancesRecord(balances: ReadonlyMap<string, Money>): Readonly<Record<string, Money>> {
  return Object.fromEntries(balances.entries());
}
